/**
 * PageShell — the ONE place page chrome is composed (F4).
 *
 * THE DEFECT THIS CLOSES. Chrome — the navbar, the desktop `Footer`, the
 * mobile `MobileBottomNav` and the `pb-16` spacer that nav owes — was
 * hand-composed in ELEVEN layout modules, and no two agreed. Measured on the
 * hermetic fleet at 393x852: the account listing OVERVIEW and the section
 * EDITOR both rendered the public footer AND the tab bar; the listings hub
 * paid a `pb-16 md:pb-0` spacer for a nav it never rendered; `estate-detail`
 * dropped the footer its siblings keep. Every one of those is the same bug —
 * a policy nobody wrote down, restated eleven times.
 *
 * `detail-layout.tsx`'s header asked for a lint ("renders `MobileBottomNav`
 * => its `main` has `pb-16`"). This is stronger: the spacer is DERIVED from
 * the same boolean that renders the nav, in one table row, so the pair cannot
 * disagree — and outside this directory the chrome components are not
 * importable at all (`no-restricted-imports` in the app's eslint config), so no
 * module can mint a twelfth composition.
 *
 * ## Why it lives HERE and not in `src/app/`
 *
 * Because `root-layout.tsx` renders it, and the generated client route table
 * — which imports `root-layout` — is compiled by the SAME Bazel target as
 * every other `src/app/*` module (`src_app_entry`, tsbuildgen's Layer 7). A
 * `PageShell.tsx` in `src/app/` therefore closes a target CYCLE:
 * `src_app_root_layout` -> `src_app_entry` -> `src_app_root_layout`. It is
 * also the honest address: this element type IS "page chrome" in the app's
 * own DAG (`src/shared/components/{layout,realtime}/**` = `infra`), and what
 * this file does is compose page chrome. The `shell` still owns the
 * composition — `root-layout.tsx` renders exactly this one component.
 *
 * ## Where the answer comes from
 *
 * `handle.chrome`, emitted STATICALLY on the route object by `webroutesgen`
 * from `web/manifests/routes.yaml`'s `chrome:` — on the layout GROUP, which
 * covers its subtree, and on the leaves that override it. Static, not through
 * the `lazy()` module: React Router would take a `handle` from a lazy module,
 * but then the shell would not know the chrome until that chunk resolved — a
 * chrome FLASH on first paint and a different answer on the server than in
 * hydration, in an app whose architecture bans viewport state in JS for
 * exactly that reason.
 *
 * ## Why the bottom edge is a table and not a judgement
 *
 * `Z_LAYERS` puts `actionBar` (z-30) UNDER `bottomNav` (z-40) deliberately —
 * an action bar must never hide the app's navigation. So a page that renders
 * both loses: the nav paints over the band, and on the listing editor that was
 * measured as `elementFromPoint` at Save's own centre hitting a nav tab. The
 * fix is not a layer swap; it is that a `focus` page renders no nav. That
 * is the `focus` row, and `page-chrome.spec.ts` asserts the pair is never
 * both visible on any representative route at either viewport.
 */
import { isPageChrome, type PageChrome } from '@/shared/lib/page-chrome_gen';
import { internalPath, type Route } from '@letsgethome/routes';
import { cn } from '@letsgethome/ui/lib/utils';
import { Outlet, useMatches } from 'react-router';

import { Footer } from './Footer';
import { MobileBottomNav } from './MobileBottomNav';
import { MobileTopBar } from './MobileTopBar';
import { SiteNavbar } from './SiteNavbar';

/**
 * What a chrome kind RENDERS. Every field is a band at a page edge, or the
 * geometry those bands and the page share; `main`'s classes are derived from
 * them rather than declared beside them, which is the whole mechanism — the
 * spacer and the nav are one row.
 */
export interface ChromeSpec {
  /** Which navbar, if any. `site` is `SiteNavbar` (itself `hidden md:block`). */
  readonly navbar: 'site' | 'none';
  /**
   * The column the navbar's content and the page SHARE.
   *
   * `contained` is the site's `max-w-7xl` reading column; `bleed` is the
   * viewport. It is one field because it is one decision: a full-bleed page
   * under a contained navbar is the defect measured on the browse hub at
   * 1600x900 — navbar inner width 1280, body width 1600.
   */
  readonly width: 'contained' | 'bleed';
  /**
   * `flow`: the DOCUMENT scrolls (the ordinary page). `fixed`: the page IS the
   * viewport and its own panes scroll inside it (a map/list canvas).
   *
   * Also measured on the hub while it was `article`: `scrollHeight` 1398
   * against a 900 viewport, i.e. 498 px of document scroll on a surface whose
   * whole point is a fixed working area.
   */
  readonly viewport: 'flow' | 'fixed';
  /** The public `Footer`, desktop only — every layout wrapped it `hidden md:block`. */
  readonly footer: boolean;
  /** The four-tab `MobileBottomNav`, mobile only (it is `md:hidden`). */
  readonly bottomTabs: boolean;
  /**
   * The MOBILE top band (`md:hidden`), and what it carries.
   *
   * `back` renders `MobileTopBar` with a history-aware `BackLink`; `none`
   * renders nothing. Below `md` the navbar is hidden, so before this column a
   * `focus`/`article` page — a listing detail, an estate, settings, a
   * conversation thread — had NO top band at all and no way back but the
   * browser's own chrome.
   *
   * ⚠ This column is not free to choose: it must equal
   * `CHROME_KINDS_WITH_BACK_TARGET`, the generator's OWN predicate for which
   * kinds owe a `back:` route id. `PageShell.react.test.tsx` asserts the two
   * are the same set.
   */
  readonly mobileHeader: 'none' | 'back';
}

/**
 * THE table. `satisfies Record<PageChrome, ChromeSpec>` over the GENERATED
 * union, so a kind added to the manifest's vocabulary does not typecheck here
 * until someone says which bands it renders — and a kind deleted there stops
 * compiling here rather than rotting as an unreachable row.
 *
 * No two rows may be equal: a distinction the manifest can express and the
 * shell cannot honour is a declaration that lies.
 */
export const PAGE_CHROME = {
  hub: {
    navbar: 'site',
    width: 'contained',
    viewport: 'flow',
    footer: true,
    bottomTabs: true,
    mobileHeader: 'none',
  },
  article: {
    navbar: 'site',
    width: 'contained',
    viewport: 'flow',
    footer: true,
    bottomTabs: false,
    mobileHeader: 'back',
  },
  focus: {
    navbar: 'site',
    width: 'contained',
    viewport: 'flow',
    footer: false,
    bottomTabs: false,
    mobileHeader: 'back',
  },
  inbox: {
    navbar: 'site',
    width: 'contained',
    viewport: 'flow',
    footer: false,
    bottomTabs: true,
    mobileHeader: 'none',
  },
  canvas: {
    navbar: 'site',
    width: 'bleed',
    viewport: 'fixed',
    footer: false,
    bottomTabs: false,
    mobileHeader: 'none',
  },
  flow: {
    navbar: 'none',
    width: 'bleed',
    viewport: 'flow',
    footer: false,
    bottomTabs: false,
    mobileHeader: 'none',
  },
} as const satisfies Record<PageChrome, ChromeSpec>;

/**
 * `main`'s class list, DERIVED.
 *
 * `flex flex-col` on every kind, unconditionally: a `bottom`-only sticky band
 * (`StickyActionBar`) is offset upward from its FLOW position and never pushed
 * down, so on a page shorter than the viewport it needs `mt-auto` in a column
 * to sit at the bottom edge. Giving it to every kind is what lets any page
 * grow one without also having to change its layout's contract (the LET-120
 * argument, made total).
 *
 * `pb-16 md:pb-0` iff the tab bar renders: the nav is `fixed`, i.e. out of
 * flow, so without the spacer it occludes the last 64 px of the page. That
 * this reads `spec.bottomTabs` — the same value that renders the nav — is the
 * point of the table.
 */
export function pageChromeMainClassName(spec: ChromeSpec): string {
  return cn(
    'flex flex-col',
    // The VIEWPORT half. `svh`, and NOT `dvh`, because this estate removed a
    // `100dvh` bound once already and wrote down why
    // (`features/wizard/pages/layout.tsx`: the wizard shell WAS `h-[100dvh]`
    // with a height-bounded `main`, and both halves went because nothing
    // STATED the bound as a contract — so the editor rendering the same step
    // forms under `min-h-screen` did not honour it and Save landed 70 px below
    // the fold — and because "iOS's `100dvh`-vs-URL-bar jitter goes with the
    // unit"). Neither reason survives here, and the second is why the unit is
    // `svh`:
    //   * the contract IS stated, by the only thing that can state it — a
    //     column of this table, declared per route in the manifest and
    //     rendered by one component. `ListingsClient` is `h-full` BECAUSE the
    //     shell bounds it; a page that wants to scroll is simply not `canvas`;
    //   * a map/list canvas is viewport-BOUNDED in a way a scrolling form is
    //     not — its map pane and its results pane are the scroll containers,
    //     and the document scrolling under them is the defect;
    //   * `svh` is the SMALL viewport, i.e. the height with the browser UI
    //     SHOWN, and it is STATIC: it does not move as the URL bar collapses,
    //     so the `dvh` jitter is unrepresentable and the canvas can never be
    //     taller than what is on screen. The cost is the `lvh - svh` strip
    //     when the bar has collapsed — empty space under panes that scroll
    //     anyway, not content pushed out of reach.
    // Below `md` the navbar is `hidden`, so `main` is the whole viewport;
    // at `md+` it sits under the sticky navbar, which is in flow.
    spec.viewport === 'fixed'
      ? 'h-svh overflow-hidden md:h-[calc(100svh-var(--navbar-height))]'
      : 'min-h-screen',
    spec.bottomTabs && 'pb-16 md:pb-0'
  );
}

/**
 * The DEEPEST matched route that declares a chrome.
 *
 * Deepest wins, so a leaf overrides its group (that is how `/profile` is a tab
 * root inside an otherwise chrome-less account subtree). React Router types
 * `handle` as `unknown`, so the narrowing is ours; an unrecognised value
 * THROWS rather than defaulting, because a default would render some chrome
 * for a route whose chrome nobody decided — the F4 defect with a new face.
 * `webroutesgen` refuses to emit a page whose chain declares nothing, so
 * reaching either throw means the generated table and this file disagree.
 *
 * Taken as a plain argument rather than read from a hook so both refusals are
 * assertable: a throw from a route ELEMENT is swallowed by React Router's own
 * error boundary, which would make the test observe an error page instead of
 * the exception, and the message is the whole value of the throw.
 */
export function pageChromeFromMatches(
  matches: readonly { readonly id: string; readonly handle: unknown }[]
): PageChrome {
  for (let i = matches.length - 1; i >= 0; i -= 1) {
    const match = matches[i];
    const handle: unknown = match?.handle;
    if (typeof handle === 'object' && handle !== null && 'chrome' in handle) {
      const { chrome } = handle as { chrome: unknown };
      if (isPageChrome(chrome)) return chrome;
      throw new Error(
        `PageShell: route "${match?.id ?? '?'}" declares handle.chrome=${JSON.stringify(chrome)}, which is not a page-chrome kind.`
      );
    }
  }
  throw new Error(
    'PageShell: no matched route declares handle.chrome. Every layout group in web/manifests/routes.yaml public_render must carry `chrome:`.'
  );
}

/**
 * Where the mobile band's back control goes when there is NO history to pop.
 *
 * Two sources, deepest match first, and DATA wins over the declaration at the
 * same depth:
 *
 *   * `loaderData.backHref` — for a leaf whose parent depends on data. The
 *     listing detail is the case: a listing's hub is the hub of ITS OWN mode,
 *     which only the loaded listing knows. It arrives as an unknown string, so
 *     it goes through `internalPath` — the one sanctioned runtime-string→Route
 *     door, which refuses a protocol-relative or absolute URL, so a loader
 *     cannot turn the back control into an open redirect.
 *   * `handle.backTo` — the manifest's `back:` row, emitted STATICALLY by
 *     `webroutesgen` as a branded `Route`, for the reason `handle.chrome` is
 *     static: no flash, and the same answer on the server as in hydration.
 *
 * `null` when neither exists. The generator REQUIRES a `back:` on every page
 * whose chrome renders the band, so `null` reaching a rendered band would mean
 * the emitted table and this file disagree — `MobileTopBar` renders no control
 * rather than one that goes nowhere.
 */
export function pageBackTargetFromMatches(
  // Spelled inline, exactly like `pageChromeFromMatches`'s parameter: this is
  // react-router's match shape, not an entity, and its `id` is a ROUTE id.
  matches: readonly { readonly handle: unknown; readonly data: unknown }[]
): Route | null {
  for (let i = matches.length - 1; i >= 0; i -= 1) {
    const match = matches[i];
    const data: unknown = match?.data;
    if (typeof data === 'object' && data !== null && 'backHref' in data) {
      const { backHref } = data as { backHref: unknown };
      if (typeof backHref === 'string') {
        const safe = internalPath(backHref);
        if (safe !== null) return safe;
      }
    }
    const handle: unknown = match?.handle;
    if (typeof handle === 'object' && handle !== null && 'backTo' in handle) {
      const { backTo } = handle as { backTo: unknown };
      if (typeof backTo === 'string') return internalPath(backTo);
    }
  }
  return null;
}

/**
 * The app shell's chrome band + the ONE `main` landmark.
 *
 * `main` is here and nowhere else — a `no-restricted-syntax` row makes a JSX
 * `<main>` outside this directory an error. Before this, eleven layouts each
 * rendered one and twenty page modules rendered a SECOND inside it, which is
 * why `web/e2e/helpers`' landmark assertion had to say `.first()` "for layouts
 * with nested mains".
 */
export function PageShell() {
  const matches = useMatches();
  const spec = PAGE_CHROME[pageChromeFromMatches(matches)];
  const backTarget = pageBackTargetFromMatches(matches);
  return (
    <>
      {spec.navbar === 'site' && <SiteNavbar width={spec.width} />}
      {/* Mobile only, and above `main` in the flow — the band is `sticky`, so
          it needs no spacer, which is why this column has no `pt-*` twin the
          way `bottomTabs` has `pb-16`. */}
      {spec.mobileHeader === 'back' && backTarget !== null && <MobileTopBar backTo={backTarget} />}
      <main className={pageChromeMainClassName(spec)}>
        <Outlet />
      </main>
      {/* Desktop only, exactly as all eleven layouts spelled it by hand. */}
      {spec.footer && (
        <div className="hidden md:block">
          <Footer />
        </div>
      )}
      {spec.bottomTabs && <MobileBottomNav />}
    </>
  );
}
